import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Grid, Paper, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Divider, TextField,
  CircularProgress
} from '@mui/material';
import {
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Phone as CallIcon,
  Email as EmailIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useTenant } from '../contexts/TenantContext';
import WhatsAppCampaigns from '../components/WhatsAppCampaigns';
import WhatsAppConnections from '../components/WhatsAppConnections';
import WhatsAppReplies from '../components/WhatsAppReplies';

interface Message {
  id: string;
  leadId: string;
  leadName: string;
  channel: string;
  type: string;
  content: string;
  status: string;
  senderName: string;
  timestamp: number;
}

const Communications: React.FC = () => {
  const { organizationId, workspaceId } = useTenant();
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId || !workspaceId) return;

    const commRef = collection(db, 'organizations', organizationId, 'workspaces', workspaceId, 'communications');
    const q = query(commRef, orderBy('timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot: any) => {
      const msgs: Message[] = [];
      snapshot.forEach((doc: any) => {
        msgs.push({ id: doc.id, ...doc.data() } as Message);
      });
      setMessages(msgs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [organizationId, workspaceId]);

  const uniqueConversations = Array.from(new Set(messages.map(m => m.leadId)))
    .map(id => messages.find(m => m.leadId === id)!);

  const selectedMessages = messages
    .filter(m => m.leadId === selectedLeadId)
    .sort((a, b) => a.timestamp - b.timestamp);

  const getChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'whatsapp': return <WhatsAppIcon sx={{ color: '#25D366' }} />;
      case 'call': return <CallIcon color="primary" />;
      case 'email': return <EmailIcon color="secondary" />;
      default: return <SendIcon />;
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Communication Hub</Typography>
      <WhatsAppCampaigns />
      <WhatsAppConnections />
      <WhatsAppReplies />
      {loading && <CircularProgress />}

      <Grid container spacing={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Inbox List */}
        <Grid item xs={4} sx={{ borderRight: '1px solid', borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.01)' }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </Box>
          <Divider />
          <List sx={{ overflowY: 'auto', height: 'calc(100% - 70px)', p: 0 }}>
            {uniqueConversations.map((conv) => (
              <ListItem
                button
                key={conv.leadId}
                selected={selectedLeadId === conv.leadId}
                onClick={() => setSelectedLeadId(conv.leadId)}
                sx={{ py: 2 }}
              >
                <ListItemAvatar>
                  <Avatar>{conv.leadName.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={conv.leadName}
                  secondary={conv.content}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                <Box sx={{ textAlign: 'right', ml: 1 }}>
                  <Typography variant="caption" color="textSecondary">
                    {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <Box>{getChannelIcon(conv.channel)}</Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Grid>

        {/* Chat Window */}
        <Grid item xs={8} sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
          {selectedLeadId ? (
            <>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>{messages.find(m => m.leadId === selectedLeadId)?.leadName.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {messages.find(m => m.leadId === selectedLeadId)?.leadName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Active Connection</Typography>
                </Box>
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedMessages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{
                      alignSelf: msg.type === 'Inbound' ? 'flex-start' : 'flex-end',
                      maxWidth: '70%'
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: msg.type === 'Inbound' ? 'rgba(255,255,255,0.05)' : 'primary.main',
                        color: msg.type === 'Inbound' ? 'text.primary' : 'primary.contrastText',
                        borderRadius: msg.type === 'Inbound' ? '0 12px 12px 12px' : '12px 0 12px 12px'
                      }}
                    >
                      <Typography variant="body2">{msg.content}</Typography>
                    </Paper>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block', textAlign: msg.type === 'Inbound' ? 'left' : 'right' }}>
                      {new Date(msg.timestamp).toLocaleTimeString()} • {msg.status}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />
              <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">
                Reply to WhatsApp conversations in the WhatsApp replies panel above.
              </Typography></Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography color="textSecondary">Select a conversation to start messaging</Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Communications;
